import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isTheme, THEME_STORAGE_KEY, ThemeContext, type Theme } from '@/lib/theme';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function readStoredTheme(): Theme {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isTheme(stored) ? stored : 'system';
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') {
    return theme;
  }
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveTheme(theme));

  useEffect(() => {
    const applied = resolveTheme(theme);
    setResolvedTheme(applied);
    document.documentElement.classList.toggle('dark', applied === 'dark');
    document.documentElement.style.colorScheme = applied;
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      const applied = event.matches ? 'dark' : 'light';
      setResolvedTheme(applied);
      document.documentElement.classList.toggle('dark', applied === 'dark');
      document.documentElement.style.colorScheme = applied;
    };

    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
